import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { OrganizationProvider } from './context/OrganizationContext.jsx';
import { OrgFinanceProvider } from './context/OrgFinanceContext.jsx';
import { ThemeProvider } from './context/ThemeContext.jsx';
import { PlatformThemeProvider } from './context/PlatformThemeContext.jsx';
import { SocketProvider } from './context/SocketContext.jsx';
import { ModeProvider } from './context/ModeContext.jsx';
import { WorkspaceStateProvider } from './context/WorkspaceStateContext.jsx';
import { ChatProvider } from './context/ChatContext.jsx';
import { HeaderProvider } from './context/HeaderContext.jsx';
import { CurrencyProvider } from './context/CurrencyContext.jsx';
import MarketingProtectedRoute from './components/auth/MarketingProtectedRoute.jsx';
import OrganizationProtectedRoute from './components/auth/OrganizationProtectedRoute.jsx';
import ToasterThemeSync from './components/common/ToasterThemeSync.jsx';
import PlanFeatureGate from './components/billing/PlanFeatureGate.jsx';
import BillingUpsellProvider from './components/billing/BillingUpsellProvider.jsx';

// Import security utilities
import security from './utils/security.js';

// Import enhanced dark theme styles
import './assets/styles/dark-theme.css';
import './assets/styles/organization-theme.css';
import './assets/styles/platform-theme.css';

// Layout components
import Layout from './components/layout/Layout.jsx';
import AuthLayout from './components/layout/AuthLayout.jsx';
import OrganizationLayout from './layouts/OrganizationLayout.jsx';
import AdminLayout from './layouts/AdminLayout.jsx';

// Auth pages (keep these loaded since they're small and often needed)
import Login from './pages/auth/Login.jsx';
import Register from './pages/auth/Register.jsx';
import RegisterOptions from './pages/auth/RegisterOptions.jsx';
import OrganizationRegister from './pages/auth/OrganizationRegister.jsx';
import ForgotPassword from './pages/auth/ForgotPassword.jsx';
import ResetPassword from './pages/auth/ResetPassword.jsx';
import AcceptInvitation from './pages/auth/AcceptInvitation.jsx';
import AcceptOrgInvitation from './pages/organization/AcceptInvitation.jsx';
import InvitePage from './pages/auth/InvitePage.jsx';
import EmailSent from './pages/auth/EmailSent.jsx';
import AuthCallback from './pages/auth/AuthCallback.jsx';
import AccountDeactivated from './pages/auth/AccountDeactivated.jsx';
import ReactivateAccount from './pages/auth/ReactivateAccount.jsx';

// Legal pages
import PrivacyPolicy from './pages/legal/PrivacyPolicy.jsx';
import TermsOfService from './pages/legal/TermsOfService.jsx';

// Product pages
import Pricing from './pages/product/Pricing.jsx';
import Integrations from './pages/product/Integrations.jsx';

// Company pages
import About from './pages/company/About.jsx';
import Contact from './pages/company/Contact.jsx';

// Support pages
import HelpCenter from './pages/support/HelpCenter.jsx';

// Documentation pages
import DocsHome from './pages/docs/DocsHome.jsx';
import GettingStarted from './pages/docs/GettingStarted.jsx';
import FeaturesOverview from './pages/docs/FeaturesOverview.jsx';
import PersonalWorkspaceDocs from './pages/docs/personal/PersonalWorkspaceDocs.jsx';
import TaskManagement from './pages/docs/personal/TaskManagement.jsx';
import ProjectManagement from './pages/docs/personal/ProjectManagement.jsx';
import PersonalTeamsDocs from './pages/docs/personal/PersonalTeams.jsx';
import PersonalTodosDocs from './pages/docs/personal/PersonalTodos.jsx';
import OrganizationWorkspaceDocs from './pages/docs/organization/OrganizationWorkspaceDocs.jsx';
import OrganizationFinance from './pages/docs/organization/OrganizationFinance.jsx';
import OrgDepartments from './pages/docs/organization/OrgDepartments.jsx';
import OrgTeams from './pages/docs/organization/OrgTeams.jsx';
import OrgUsers from './pages/docs/organization/OrgUsers.jsx';
import OrgAnalytics from './pages/docs/organization/OrgAnalytics.jsx';
import OrgBilling from './pages/docs/organization/OrgBilling.jsx';
import AdminDocs from './pages/docs/admin/AdminDocs.jsx';
import RoleHierarchy from './pages/docs/admin/RoleHierarchy.jsx';
import AdminPermissions from './pages/docs/admin/AdminPermissions.jsx';
import AdminOrgAdmin from './pages/docs/admin/AdminOrgAdmin.jsx';
import AdminSecurity from './pages/docs/admin/AdminSecurity.jsx';
import ViewsBoards from './pages/docs/features/ViewsBoards.jsx';
import CalendarDocs from './pages/docs/features/CalendarDocs.jsx';
import NotificationsDocs from './pages/docs/features/NotificationsDocs.jsx';
import AnalyticsDocs from './pages/docs/features/AnalyticsDocs.jsx';
import AIDocs from './pages/docs/features/AIDocs.jsx';
import TimelineDocs from './pages/docs/features/TimelineDocs.jsx';
import DependenciesDocs from './pages/docs/features/DependenciesDocs.jsx';

// Protected route component
import ProtectedRoute from './components/common/ProtectedRoute.jsx';
import RoleBasedRoute from './components/common/RoleBasedRoute.jsx';
import ScrollToTop from './components/common/ScrollToTop.jsx';

// AI Assistant component (Phase 4 — replaces legacy Chatbot)
import AIAssistantWidget from './components/AIAssistant/AIAssistantWidget.jsx';

// Lazy load heavy components
const Home = lazy(() => import('./pages/Home.jsx'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard.jsx'));
const ProjectsList = lazy(() => import('./pages/projects/ProjectsList.jsx'));
const ProjectDetails = lazy(() => import('./pages/projects/ProjectDetails.jsx'));
const CreateProject = lazy(() => import('./pages/projects/CreateProject.jsx'));
const EditProject = lazy(() => import('./pages/projects/EditProject.jsx'));
const TaskDetails = lazy(() => import('./pages/tasks/TaskDetails.jsx'));
const CreateTask = lazy(() => import('./pages/tasks/CreateTask.jsx'));
const EditTask = lazy(() => import('./pages/tasks/EditTask.jsx'));
const TaskCalendar = lazy(() => import('./pages/tasks/TaskCalendar.jsx'));
const MyTasks = lazy(() => import('./pages/tasks/MyTasks.jsx'));
const TimelineView = lazy(() => import('./pages/tasks/TimelineView.jsx'));
const MessagesPage = lazy(() => import('./pages/messages/MessagesPage.jsx'));
const StickyNotesDashboard = lazy(() => import('./pages/sticky-notes/StickyNotesDashboard.jsx'));
const UserProfile = lazy(() => import('./pages/profile/UserProfile.jsx'));
const Settings = lazy(() => import('./pages/settings/Settings.jsx'));
const UserManagement = lazy(() => import('./pages/users/UserManagement.jsx'));
const Reports = lazy(() => import('./pages/reports/Reports.jsx'));
const MarketingDashboard = lazy(() => import('./pages/marketing/MarketingDashboard.jsx'));

// Personal Notifications
const PersonalNotificationsPage = lazy(() => import('./pages/notifications/PersonalNotificationsPage.jsx'));

// Invitations
const InvitationsPage = lazy(() => import('./pages/invitations/InvitationsPage.jsx'));

// Personal Teams
const PersonalTeams = lazy(() => import('./pages/PersonalTeams.jsx'));
const TeamInvitationAccept = lazy(() => import('./pages/TeamInvitationAccept.jsx'));

// Enterprise Organization Management Pages
const OrganizationDashboard = lazy(() => import('./pages/organization/OrganizationDashboard.jsx'));
const OrganizationSettings = lazy(() => import('./pages/organization/OrganizationSettings.jsx'));
const OrganizationDepartments = lazy(() => import('./pages/organization/OrganizationDepartments.jsx'));
const DepartmentManager = lazy(() => import('./pages/organization/DepartmentManager.jsx'));
const OrganizationBrandingSettings = lazy(() => import('./pages/organization/OrganizationBrandingSettings.jsx'));
const OrganizationUsers = lazy(() => import('./pages/organization/OrganizationUsers.jsx'));
const OrganizationProjectsList = lazy(() => import('./pages/organization/OrganizationProjectsList.jsx'));
const OrganizationTasksList = lazy(() => import('./pages/organization/OrganizationTasksList.jsx'));
const OrganizationTimelineView = lazy(() => import('./pages/organization/OrganizationTimelineView.jsx'));
const OrganizationAppStore = lazy(() => import('./pages/organization/OrganizationAppStore.jsx'));
const OrganizationActivity = lazy(() => import('./pages/organization/OrganizationActivity.jsx'));
const UserInvitation = lazy(() => import('./pages/organization/UserInvitation.jsx'));

// Phase 2: Department Management System
const DepartmentList = lazy(() => import('./pages/organization/departments/DepartmentList.jsx'));
const DepartmentDetails = lazy(() => import('./pages/organization/departments/DepartmentDetails.jsx'));
const CreateDepartment = lazy(() => import('./pages/organization/departments/CreateDepartment.jsx'));
const EditDepartment = lazy(() => import('./pages/organization/departments/EditDepartment.jsx'));
const DepartmentAnalytics = lazy(() => import('./pages/organization/departments/DepartmentAnalytics.jsx'));

// Phase 3: Team Management System
const TeamList = lazy(() => import('./pages/organization/teams/TeamList.jsx'));
const TeamDetails = lazy(() => import('./pages/organization/teams/TeamDetails.jsx'));
const CreateTeam = lazy(() => import('./pages/organization/teams/CreateTeam.jsx'));
const EditTeam = lazy(() => import('./pages/organization/teams/EditTeam.jsx'));

// Notifications
const OrgNotificationsPage = lazy(() => import('./pages/organization/OrgNotificationsPage.jsx'));

// Analytics & Reporting
const OrganizationAnalytics = lazy(() => import('./pages/organization/OrganizationAnalytics.jsx'));
const OrganizationReports = lazy(() => import('./pages/organization/OrganizationReports.jsx'));

// Organization Finance App (Phase 3) - Comprehensive Dashboard
const OrgFinanceDashboard = lazy(() => import('./pages/organization/finance/ComprehensiveFinanceDashboard.jsx'));
const DepartmentBudgetsPage = lazy(() => import('./pages/organization/finance/DepartmentBudgetsPage.jsx'));
const BudgetRequestsPage = lazy(() => import('./pages/organization/finance/BudgetRequestsPage.jsx'));
const BudgetRequestDetailPage = lazy(() => import('./pages/organization/finance/BudgetRequestDetailPage.jsx'));
const EmployeesFinancePage = lazy(() => import('./pages/organization/finance/EmployeesFinancePage.jsx'));
const FinanceAnalyticsPage = lazy(() => import('./pages/organization/finance/FinanceAnalyticsPage.jsx'));
const SubmitBudgetRequest = lazy(() => import('./pages/organization/finance/SubmitBudgetRequest.jsx'));

const SystemTestSuite = lazy(() => import('./components/organization/SystemTestSuite.jsx'));
const SecurityCompliance = lazy(() => import('./pages/organization/SecurityCompliance.jsx'));
const BillingSubscription = lazy(() => import('./pages/organization/BillingSubscription.jsx'));
const CheckoutPage = lazy(() => import('./pages/organization/CheckoutPage.jsx'));
const CheckoutSuccess = lazy(() => import('./pages/organization/CheckoutSuccess.jsx'));
const CheckoutCancel = lazy(() => import('./pages/organization/CheckoutCancel.jsx'));

// Admin Panel Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard.jsx'));
const OrgAdminDashboard = lazy(() => import('./pages/admin/OrgAdminDashboard.jsx'));

// Platform Admin Layout
const PlatformAdminLayout = lazy(() => import('./layouts/PlatformAdminLayout.jsx'));

// Platform Admin Pages
const PlatformDashboard = lazy(() => import('./pages/platform/PlatformDashboard.jsx'));
const PlatformOrganizations = lazy(() => import('./pages/platform/PlatformOrganizations.jsx'));
const OrganizationDetails = lazy(() => import('./pages/platform/OrganizationDetails.jsx'));
const PlatformUsers = lazy(() => import('./pages/platform/PlatformUsers.jsx'));
const PlatformSystem = lazy(() => import('./pages/platform/PlatformSystem.jsx'));
const PlatformSecurity = lazy(() => import('./pages/platform/PlatformSecurity.jsx'));
const PlatformAnalytics = lazy(() => import('./pages/platform/PlatformAnalytics.jsx'));
const AIAnalyticsDashboard = lazy(() => import('./pages/platform/AIAnalyticsDashboard.jsx'));
const PlatformSettings = lazy(() => import('./pages/platform/PlatformSettings.jsx'));
const PlatformNotifications = lazy(() => import('./pages/platform/PlatformNotifications.jsx'));
const PlatformPermissions = lazy(() => import('./pages/platform/PlatformPermissions.jsx'));
const PlatformThemeCustomization = lazy(() => import('./pages/platform/PlatformThemeCustomization.jsx'));
const PlatformBilling = lazy(() => import('./pages/platform/PlatformBilling.jsx'));
const PlatformCoupons = lazy(() => import('./pages/platform/PlatformCoupons.jsx'));

// Loading component for Suspense
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
  </div>
);

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize security measures
    security.init();
    
    // Simulate initial app loading
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Add this helper component inside App
  function ResetPasswordWithQuery() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    // Render ResetPassword with the token as a prop
    return <ResetPassword resettoken={token} />;
  }

  // Component to handle root route redirection based on auth status
  function RootRedirect() {
    const { currentUser, loading } = useAuth();
    
    if (loading) {
      return <LoadingSpinner />;
    }
    
    // If user is authenticated, always redirect to individual dashboard first
    // Users can manually switch to organization mode if they have access
    if (currentUser) {
      return <Navigate to="/dashboard" replace />;
    }
    
    // If not authenticated, redirect to home page
    return <Navigate to="/home" replace />;
  }

  return (
    <AuthProvider>
      <OrganizationProvider>
        <ThemeProvider>
          <ToasterThemeSync />
          <ScrollToTop />
          <SocketProvider>
            <HeaderProvider>
              <CurrencyProvider>
                <ModeProvider>
                  <WorkspaceStateProvider>
                  <ChatProvider>
                  <BillingUpsellProvider>
                <Suspense fallback={<LoadingSpinner />}>
          <Routes>
          {/* Public Home Route */}
          <Route path="/home" element={<Home />} />
          
          {/* Legal Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* Product Pages */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/integrations" element={<Integrations />} />
          
          {/* Company Pages */}
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          
          {/* Support Pages */}
          <Route path="/help" element={<HelpCenter />} />
          
          {/* Documentation Pages */}
          <Route path="/docs" element={<DocsHome />} />
          <Route path="/docs/getting-started" element={<GettingStarted />} />
          <Route path="/docs/features" element={<FeaturesOverview />} />
          <Route path="/docs/features/views" element={<ViewsBoards />} />
          <Route path="/docs/features/calendar" element={<CalendarDocs />} />
          <Route path="/docs/features/notifications" element={<NotificationsDocs />} />
          <Route path="/docs/features/analytics" element={<AnalyticsDocs />} />
          <Route path="/docs/features/ai" element={<AIDocs />} />
          <Route path="/docs/features/timeline" element={<TimelineDocs />} />
          <Route path="/docs/features/dependencies" element={<DependenciesDocs />} />
          <Route path="/docs/personal" element={<PersonalWorkspaceDocs />} />
          <Route path="/docs/personal/tasks" element={<TaskManagement />} />
          <Route path="/docs/personal/projects" element={<ProjectManagement />} />
          <Route path="/docs/personal/teams" element={<PersonalTeamsDocs />} />
          <Route path="/docs/personal/todos" element={<PersonalTodosDocs />} />
          <Route path="/docs/organization" element={<OrganizationWorkspaceDocs />} />
          <Route path="/docs/organization/finance" element={<OrganizationFinance />} />
          <Route path="/docs/organization/departments" element={<OrgDepartments />} />
          <Route path="/docs/organization/teams" element={<OrgTeams />} />
          <Route path="/docs/organization/users" element={<OrgUsers />} />
          <Route path="/docs/organization/analytics" element={<OrgAnalytics />} />
          <Route path="/docs/organization/billing" element={<OrgBilling />} />
          <Route path="/docs/admin" element={<AdminDocs />} />
          <Route path="/docs/admin/roles" element={<RoleHierarchy />} />
          <Route path="/docs/admin/permissions" element={<AdminPermissions />} />
          <Route path="/docs/admin/org-admin" element={<AdminOrgAdmin />} />
          <Route path="/docs/admin/security" element={<AdminSecurity />} />
          
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/register-options" element={<RegisterOptions />} />
            <Route path="/auth/register" element={<Register />} />
            <Route path="/auth/register-organization" element={<OrganizationRegister />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/email-sent" element={<EmailSent />} />
            <Route path="/auth/reset-password/:resettoken" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPasswordWithQuery />} />
          </Route>

          {/* Account Status Routes (no auth layout) */}
          <Route path="/account-deactivated" element={<AccountDeactivated />} />
          <Route path="/reactivate-account/:token" element={<ReactivateAccount />} />

          {/* Public invitation acceptance routes */}
          <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
          <Route path="/invite/:token" element={<AcceptOrgInvitation />} />
          
          {/* Personal Team Invitation Route - Public for viewing, protected actions */}
          <Route path="/team/invite/accept/:token" element={<TeamInvitationAccept />} />
          
          {/* New streamlined invitation route */}
          <Route path="/invite" element={<InvitePage />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            
            {/* Messages */}
            <Route path="/messages" element={<MessagesPage />} />
            
            {/* Projects */}
            <Route path="/projects" element={<ProjectsList />} />
            <Route path="/projects/:id" element={<ProjectDetails />} />
            <Route path="/projects/:id/milestones" element={<ProjectDetails />} />
            <Route 
              path="/projects/create" 
              element={
                <RoleBasedRoute allowedRoles={['user']}>
                  <CreateProject />
                </RoleBasedRoute>
              } 
            />
            <Route 
              path="/projects/:id/edit" 
              element={
                <RoleBasedRoute allowedRoles={['user']}>
                  <EditProject />
                </RoleBasedRoute>
              } 
            />
          
          {/* Tasks */}
          <Route path="/tasks" element={<MyTasks />} />
          <Route path="/tasks/timeline" element={<PlanFeatureGate featureKey="timeline_view" featureName="Timeline / Gantt view" workspace="personal"><TimelineView /></PlanFeatureGate>} />
          <Route path="/tasks/:id" element={<TaskDetails />} />
          <Route path="/tasks/:id/edit" element={<EditTask />} />
          <Route path="/tasks/create" element={<CreateTask />} />
          <Route path="/projects/:projectId/tasks/create" element={<CreateTask />} />
          <Route path="/projects/:projectId/tasks/:id" element={<TaskDetails />} />
          <Route path="/tasks/calendar" element={<TaskCalendar />} />
          
          {/* Sticky Notes */}
          <Route path="/sticky-notes" element={<PlanFeatureGate featureKey="sticky_notes" featureName="Sticky Notes" workspace="personal"><StickyNotesDashboard /></PlanFeatureGate>} />
          <Route path="/sticky-notes/personal" element={<PlanFeatureGate featureKey="sticky_notes" featureName="Sticky Notes" workspace="personal"><StickyNotesDashboard /></PlanFeatureGate>} />
          <Route path="/sticky-notes/shared" element={<PlanFeatureGate featureKey="sticky_notes" featureName="Sticky Notes" workspace="personal"><StickyNotesDashboard /></PlanFeatureGate>} />
          <Route path="/todos" element={<Navigate to="/sticky-notes" replace />} />
          <Route path="/todos/personal" element={<Navigate to="/sticky-notes/personal" replace />} />
          <Route path="/todos/shared" element={<Navigate to="/sticky-notes/shared" replace />} />
          
          {/* Personal Notifications */}
          <Route path="/personal/notifications" element={<PersonalNotificationsPage />} />
          
          {/* Pending Invitations */}
          <Route path="/personal/invitations" element={<InvitationsPage />} />
          
          {/* Personal Teams */}
          <Route path="/personal/teams" element={<PersonalTeams />} />

          {/* Personal Billing */}
          <Route path="/billing" element={<BillingSubscription workspace="personal" />} />
          <Route path="/billing/checkout" element={<CheckoutPage workspace="personal" />} />
          <Route path="/billing/success" element={<CheckoutSuccess workspace="personal" />} />
          <Route path="/billing/cancel" element={<CheckoutCancel workspace="personal" />} />
          
          {/* User Management */}
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/settings" element={<Settings />} />
          <Route 
            path="/users" 
            element={
              <RoleBasedRoute allowedRoles={['platform_admin', 'super_admin']}>
                <UserManagement />
              </RoleBasedRoute>
            } 
          />
          
          {/* Reports & Analytics */}
          <Route 
            path="/reports" 
            element={
              <RoleBasedRoute allowedRoles={['platform_admin', 'super_admin']}>
                <Reports />
              </RoleBasedRoute>
            } 
          />
          
          {/* Marketing Management */}
          <Route 
            path="/marketing" 
            element={
              <RoleBasedRoute allowedRoles={['platform_admin', 'super_admin']}>
                <MarketingProtectedRoute>
                  <MarketingDashboard />
                </MarketingProtectedRoute>
              </RoleBasedRoute>
            } 
          />
          
          {/* Marketing Content Management */}
          <Route 
            path="/marketing/content" 
            element={
              <RoleBasedRoute allowedRoles={['platform_admin', 'super_admin']}>
                <MarketingProtectedRoute>
                  <MarketingDashboard />
                </MarketingProtectedRoute>
              </RoleBasedRoute>
            } 
          />
        </Route>
          
          {/* Organization-specific Routes - OUTSIDE Layout wrapper to avoid double sidebar */}
          <Route path="/org/*" element={
            <OrganizationProtectedRoute requireOrganizationAccess={false}>
              <OrgFinanceProvider>
                <OrganizationLayout>
                  <Routes>
                  <Route index element={<Navigate to="/org/dashboard" replace />} />
                  <Route path="dashboard" element={<OrganizationDashboard />} />
                  
                  {/* Messages */}
                  <Route path="messages" element={<MessagesPage />} />
                  
                  {/* Phase 2: Department Management Routes */}
                  <Route path="departments" element={<DepartmentList />} />
                  <Route path="departments/create" element={<CreateDepartment />} />
                  <Route path="departments/:deptId" element={<DepartmentDetails />} />
                  <Route path="departments/:deptId/edit" element={<EditDepartment />} />
                  <Route path="departments/:deptId/analytics" element={<DepartmentAnalytics />} />
                  
                  {/* Legacy department routes */}
                  <Route path="departments/legacy" element={<OrganizationDepartments />} />
                  <Route path="departments/manager" element={<DepartmentManager />} />
                  
                  {/* Phase 3: Team Management Routes */}
                  <Route path="teams" element={<TeamList />} />
                  <Route path="teams/create" element={<CreateTeam />} />
                  <Route path="teams/:teamId" element={<TeamDetails />} />
                  <Route path="teams/:teamId/edit" element={<EditTeam />} />
                  
                  <Route path="users" element={<OrganizationUsers />} />
                  <Route path="invite" element={<UserInvitation />} />
                  <Route path="activity" element={<PlanFeatureGate featureKey="audit_logs" featureName="Activity history" workspace="organization"><OrganizationActivity /></PlanFeatureGate>} />
                  <Route path="projects" element={<OrganizationProjectsList />} />
                  <Route path="projects/:id" element={<ProjectDetails />} />
                  <Route path="projects/:id/edit" element={<EditProject />} />
                  <Route path="projects/create" element={<CreateProject />} />
                  <Route path="tasks" element={<OrganizationTasksList />} />
                  <Route path="tasks/timeline" element={<PlanFeatureGate featureKey="timeline_view" featureName="Timeline / Gantt view" workspace="organization"><OrganizationTimelineView /></PlanFeatureGate>} />
                  <Route path="tasks/:id" element={<TaskDetails />} />
                  <Route path="tasks/:id/edit" element={<EditTask />} />
                  <Route path="tasks/create" element={<CreateTask />} />
                  <Route path="projects/:projectId/tasks/create" element={<CreateTask />} />
                  <Route path="app-store" element={<OrganizationAppStore />} />
                  
                  {/* Organization Finance App Routes - Full Featured Dashboard */}
                  <Route path="finance" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><OrgFinanceDashboard /></PlanFeatureGate>} />
                  <Route path="finance/pending" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><BudgetRequestsPage filter="pending" /></PlanFeatureGate>} />
                  <Route path="finance/requests" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><BudgetRequestsPage /></PlanFeatureGate>} />
                  <Route path="finance/requests/new" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><SubmitBudgetRequest /></PlanFeatureGate>} />
                  <Route path="finance/requests/:id" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><BudgetRequestDetailPage /></PlanFeatureGate>} />
                  <Route path="finance/my-requests" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><BudgetRequestsPage filter="my" /></PlanFeatureGate>} />
                  <Route path="finance/submit-request" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><SubmitBudgetRequest /></PlanFeatureGate>} />
                  <Route path="finance/my-budget" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><DepartmentBudgetsPage filter="my" /></PlanFeatureGate>} />
                  <Route path="finance/budgets" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><DepartmentBudgetsPage /></PlanFeatureGate>} />
                  <Route path="finance/employees" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><EmployeesFinancePage /></PlanFeatureGate>} />
                  <Route path="finance/analytics" element={<PlanFeatureGate featureKey="module_finance" featureName="Finance module" workspace="organization"><FinanceAnalyticsPage /></PlanFeatureGate>} />
                  
                  {/* Marketing Routes */}
                  <Route path="marketing" element={<MarketingDashboard />} />
                  
                  {/* Removed duplicate /teams route - TeamList already defined at line 325 */}
                  <Route path="analytics" element={<OrganizationAnalytics />} />
                  <Route path="reports" element={<OrganizationReports />} />
                  <Route path="settings" element={<OrganizationBrandingSettings />} />
                  <Route path="settings/legacy" element={<OrganizationSettings />} />
                  <Route path="security" element={<SecurityCompliance />} />
                  <Route path="billing" element={<BillingSubscription workspace="organization" />} />
                  <Route path="billing/checkout" element={<CheckoutPage workspace="organization" />} />
                  <Route path="billing/success" element={<CheckoutSuccess workspace="organization" />} />
                  <Route path="billing/cancel" element={<CheckoutCancel workspace="organization" />} />
                  <Route path="test-suite" element={<SystemTestSuite />} />
                  <Route path="notifications" element={<OrgNotificationsPage />} />
                  <Route path="profile" element={<UserProfile />} />
                </Routes>
              </OrganizationLayout>
            </OrgFinanceProvider>
            </OrganizationProtectedRoute>
          } />

          {/* Organization Admin Panel - NEW with AdminLayout */}
          <Route path="/org/admin" element={
            <RoleBasedRoute allowedRoles={['org_admin']}>
              <AdminLayout />
            </RoleBasedRoute>
          }>
            <Route index element={<OrgAdminDashboard />} />
            <Route path="members" element={<OrganizationUsers />} />
            <Route path="invite" element={<UserInvitation />} />
            <Route path="departments" element={<DepartmentList />} />
            <Route path="analytics" element={<div className="text-center py-12 text-gray-500">Analytics - Coming Soon</div>} />
            <Route path="settings" element={<OrganizationSettings />} />
            <Route path="security" element={<SecurityCompliance />} />
            <Route path="billing" element={<BillingSubscription />} />
          </Route>

          {/* Platform Admin Routes - With Layout */}
          <Route path="/platform/admin" element={
            <RoleBasedRoute allowedRoles={['platform_admin', 'super_admin']}>
              <PlatformThemeProvider>
                <PlatformAdminLayout />
              </PlatformThemeProvider>
            </RoleBasedRoute>
          }>
            <Route index element={<Navigate to="/platform/admin/dashboard" replace />} />
            <Route path="dashboard" element={<PlatformDashboard />} />
            <Route path="organizations" element={<PlatformOrganizations />} />
            <Route path="organizations/:orgId" element={<OrganizationDetails />} />
            <Route path="users" element={<PlatformUsers />} />
            <Route path="system" element={<PlatformSystem />} />
            <Route path="security" element={<PlatformSecurity />} />
            <Route path="analytics" element={<PlatformAnalytics />} />
            <Route path="ai-analytics" element={<AIAnalyticsDashboard />} />
            <Route path="settings" element={<PlatformSettings />} />
            <Route path="notifications" element={<PlatformNotifications />} />
            <Route path="permissions" element={<PlatformPermissions />} />
            <Route path="theme" element={<PlatformThemeCustomization />} />
            <Route path="billing" element={<PlatformBilling />} />
            <Route path="coupons" element={<PlatformCoupons />} />
          </Route>
          
          {/* Testing Routes - Only for development */}
          {/* Widget test route removed during cleanup */}

        {/* Default route redirect based on authentication */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* AI Assistant Widget - only show on authenticated pages */}
      <AuthenticatedAIAssistant />
      
                </Suspense>
                </BillingUpsellProvider>
                  </ChatProvider>
                  </WorkspaceStateProvider>
                </ModeProvider>
              </CurrencyProvider>
            </HeaderProvider>
          </SocketProvider>
        </ThemeProvider>
      </OrganizationProvider>
    </AuthProvider>
  );
}

// Component to conditionally show AI Assistant only for authenticated users
function AuthenticatedAIAssistant() {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  // Don't show on auth pages, home page, or legal pages
  const excludedPaths = ['/home', '/auth', '/privacy', '/terms', '/accept-invitation', '/invite'];
  const isExcludedPath = excludedPaths.some(path => location.pathname.startsWith(path));
  
  if (!currentUser || isExcludedPath) {
    return null;
  }
  
  return <AIAssistantWidget />;
}

export default App;
